import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { getImageUri } from "../core/utils/imageLoad";
import { useCategories } from "../hooks/category"; // Importamos tu hook
import type { CarouselSlide } from "../core/types/tsx/components/carouselslide.types";
import type { CategoryResponse } from "../core/types/category"; // Importamos el tipo Category

export function Carouselslide() {
	// 1. Reemplazamos el fetch manual y los estados locales por el hook
	const { data: categoryData, loading } = useCategories();

	// 2. Transformamos la data usando useMemo para evitar recálculos
	const slides: CarouselSlide[] = useMemo(() => {
		if (!categoryData || !Array.isArray(categoryData)) return [];

		return categoryData
			.filter((cat: CategoryResponse) => cat.is_active) // Filtramos solo activos
			.map((cat: CategoryResponse, index: number) => {
				const fullImageUrl = getImageUri(cat.picture_category) ?? "";

				return {
					id: cat.id || `${cat.name_category}-${index}`,
					title: cat.name_category,
					subtitle: cat.description_category,
					imageSrc: fullImageUrl,
				};
			});
	}, [categoryData]);

	// --- LÓGICA DEL CARRUSEL ---
	const resolvedSlides = slides;
	const loopEnabled = resolvedSlides.length > 1;

	const loopedSlides = useMemo(() => {
		if (!loopEnabled) return resolvedSlides;
		const first = resolvedSlides[0];
		const last = resolvedSlides[resolvedSlides.length - 1];
		return [last, ...resolvedSlides, first];
	}, [loopEnabled, resolvedSlides]);

	const viewportRef = useRef<HTMLDivElement | null>(null);
	const [activeIndex, setActiveIndex] = useState(1);
	const [slideWidth, setSlideWidth] = useState(0);
	const [isPaused, setIsPaused] = useState(false);
	const autoScrollInterval = useRef<ReturnType<typeof setInterval> | null>(null);

	// Reiniciar índice cuando cargan los datos
	useEffect(() => {
		setActiveIndex(loopEnabled ? 1 : 0);
	}, [loopEnabled, slides.length]);

	useEffect(() => {
		const el = viewportRef.current;
		if (!el) return;

		const updateWidth = () => setSlideWidth(el.clientWidth);
		updateWidth();

		const ro = new ResizeObserver(updateWidth);
		ro.observe(el);
		return () => ro.disconnect();
	}, [loading]);

	useEffect(() => {
		const el = viewportRef.current;
		if (!el || slideWidth <= 0) return;

		let raf = 0;
		const onScroll = () => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(() => {
				const index = Math.round(el.scrollLeft / slideWidth);
				setActiveIndex((prev) => (prev !== index ? index : prev));
			});
		};

		el.addEventListener("scroll", onScroll, { passive: true });
		return () => {
			cancelAnimationFrame(raf);
			el.removeEventListener("scroll", onScroll);
		};
	}, [slideWidth]);

	const scrollToIndex = useCallback(
		(index: number, behavior: ScrollBehavior = "smooth") => {
			const el = viewportRef.current;
			if (!el || slideWidth <= 0) return;

			const maxScrollIndex = loopedSlides.length - 1;
			const targetIndex = Math.min(Math.max(index, 0), maxScrollIndex);

			el.scrollTo({ left: targetIndex * slideWidth, behavior });
		},
		[slideWidth, loopedSlides.length],
	);

	// Efecto para el Loop Infinito
	useEffect(() => {
		if (!loopEnabled) return;
		const maxIndex = loopedSlides.length - 1;

		if (activeIndex === 0) {
			const handle = setTimeout(() => {
				scrollToIndex(maxIndex - 1, "auto");
			}, 200);
			return () => clearTimeout(handle);
		}

		if (activeIndex === maxIndex) {
			const handle = setTimeout(() => {
				scrollToIndex(1, "auto");
			}, 200);
			return () => clearTimeout(handle);
		}
	}, [activeIndex, loopEnabled, loopedSlides.length, scrollToIndex]);

	// Efecto inicial
	useEffect(() => {
		if (slideWidth <= 0 || loading) return;
		if (!loopEnabled) {
			scrollToIndex(0, "auto");
			return;
		}
		const timer = setTimeout(() => scrollToIndex(1, "auto"), 50);
		return () => clearTimeout(timer);
	}, [slideWidth, loopEnabled, scrollToIndex, loading]);

	// Auto-scroll
	useEffect(() => {
		if (!loopEnabled || isPaused || loading) {
			if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
			return;
		}
		autoScrollInterval.current = setInterval(() => {
			scrollToIndex(activeIndex + 1, "smooth");
		}, 4500);
		return () => {
			if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
		};
	}, [loopEnabled, isPaused, activeIndex, scrollToIndex, loading]);

	// --- RENDERIZADO ---
	if (loading) {
		return (
			<div className='w-full h-64 sm:h-80 lg:h-88 flex items-center justify-center bg-neutral-50 rounded-3xl outline outline-gray-200'>
				<div className='flex flex-col items-center gap-2 text-neutral-500'>
					<Loader2 className='h-8 w-8 animate-spin' />
					<p>Cargando destacados...</p>
				</div>
			</div>
		);
	}

	if (slides.length === 0) {
		return null;
	}

	const canNavigate = loopEnabled;
	const dotIndex = loopEnabled
		? (activeIndex - 1 + resolvedSlides.length) % resolvedSlides.length
		: activeIndex;

	return (
		<div className='w-full '>
			<div
				className='relative w-full overflow-hidden rounded-3xl bg-neutral-50 outline outline-gray-200'
				onMouseEnter={() => setIsPaused(true)}
				onMouseLeave={() => setIsPaused(false)}
				onTouchStart={() => setIsPaused(true)}
				onTouchEnd={() => setIsPaused(false)}
			>
				{canNavigate && (
					<button
						type='button'
						aria-label='Anterior'
						onClick={() => scrollToIndex(activeIndex - 1)}
						className='absolute left-4 top-1/2 z-30 hidden -translate-y-1/2 rounded-full bg-neutral-50/80 p-2 outline-1 outline-white transition-colors hover:bg-white sm:block cursor-pointer'
					>
						<ChevronLeft className='h-6 w-6 text-neutral-900' />
					</button>
				)}

				{canNavigate && (
					<button
						type='button'
						aria-label='Siguiente'
						onClick={() => scrollToIndex(activeIndex + 1)}
						className='absolute right-4 top-1/2 z-30 hidden -translate-y-1/2 rounded-full bg-neutral-50/80 p-2 outline-1 outline-white transition-colors hover:bg-white sm:block cursor-pointer'
					>
						<ChevronRight className='h-6 w-6 text-neutral-900' />
					</button>
				)}

				<div
					ref={viewportRef}
					className='carousel-scroll-container flex w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-smooth hide-scrollbar touch-pan-x'
					style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
				>
					{loopedSlides.map((slide, idx) => (
						<div
							key={`${slide.id}-${idx}`}
							className='w-full min-w-full shrink-0 snap-start'
						>
							<div className='relative h-64 w-full sm:h-80 lg:h-88'>
								<div className='absolute inset-0 md:hidden'>
									{slide.imageSrc ? (
										<img
											className='h-full w-full object-cover'
											src={slide.imageSrc}
											alt={slide.title}
											loading='lazy'
											onError={(e) => {
												e.currentTarget.style.display = "none";
											}}
										/>
									) : (
										<div className='flex h-full w-full items-center justify-center bg-neutral-200 text-neutral-400'>
											Sin Imagen
										</div>
									)}
									<div className='absolute inset-0 bg-linear-to-t from-black/50 via-black/10 to-transparent' />
									<div className='absolute inset-x-3 bottom-3 z-10 rounded-xl bg-white/90 p-3 backdrop-blur-xs'>
										<div className='line-clamp-2 text-lg font-semibold leading-tight text-primary'>
											{slide.title}
										</div>
										<div className='mt-1 line-clamp-2 text-sm leading-5 text-neutral-700'>
											{slide.subtitle}
										</div>
									</div>
								</div>

								<div className='absolute inset-0 hidden md:flex'>
									<div className='relative h-full w-1/2'>
										<div className='absolute left-14 top-1/2 z-10 w-118 -translate-y-1/2 rounded-2xl bg-secondary px-4 py-3 sm:left-16 sm:px-5 sm:py-4'>
											<div className='line-clamp-2 text-3xl font-semibold leading-tight text-primary sm:text-4xl lg:text-5xl'>
												{slide.title}
											</div>
											<div className='mt-2 line-clamp-3 text-base leading-6 text-neutral-700 sm:text-lg sm:leading-7'>
												{slide.subtitle}
											</div>
										</div>
									</div>

									<div className='relative h-full w-1/2 overflow-visible'>
										{slide.imageSrc ? (
											<img
												className='carouselslide-moon-mask absolute inset-y-0 right-0 h-full w-full object-cover'
												src={slide.imageSrc}
												alt={slide.title}
												loading='lazy'
												onError={(e) => {
													e.currentTarget.style.display =
														"none";
												}}
											/>
										) : (
											<div className='flex h-full w-full items-center justify-center bg-neutral-200 text-neutral-400'>
												Sin Imagen
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* --- INDICADORES ESTILO INSTAGRAM --- */}
			<div className='mt-3 flex w-full justify-center items-center gap-1.5 h-4'>
				{(() => {
					const MAX_VISIBLE_DOTS = 5; // Instagram maneja 5 para dar ese efecto de 3 principales y 2 bordes pequeños
					const totalDots = resolvedSlides.length;

					// Lógica para calcular qué ventana de dots renderizar
					let startIndex = Math.max(0, dotIndex - 2);
					startIndex = Math.min(
						startIndex,
						Math.max(0, totalDots - MAX_VISIBLE_DOTS),
					);

					const visibleIndices = Array.from(
						{ length: Math.min(MAX_VISIBLE_DOTS, totalDots) },
						(_, i) => startIndex + i,
					);

					return visibleIndices.map((originalIndex) => {
						const isCurrent = originalIndex === dotIndex;
						const isEdgeStart =
							originalIndex === startIndex && startIndex > 0;
						const isEdgeEnd =
							originalIndex === startIndex + MAX_VISIBLE_DOTS - 1 &&
							startIndex + MAX_VISIBLE_DOTS < totalDots;

						// Determinar el tamaño (los bordes se ven más pequeños para insinuar que hay más imágenes)
						let sizeClass = "h-2 w-2";
						if (isEdgeStart || isEdgeEnd) {
							sizeClass = "h-1 w-1 opacity-60";
						}

						return (
							<button
								key={originalIndex}
								type='button'
								aria-label={
									isCurrent
										? `Slide actual ${originalIndex + 1}`
										: `Ir al slide ${originalIndex + 1}`
								}
								onClick={() =>
									scrollToIndex(
										loopEnabled ? originalIndex + 1 : originalIndex,
									)
								}
								className={
									`rounded-full transition-all duration-300 cursor-pointer ${sizeClass} ` +
									(isCurrent ? "bg-primary" : "bg-primary/20")
								}
							/>
						);
					});
				})()}
			</div>
		</div>
	);
}
